import './index-global.less';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { IReactionDisposer, reaction } from 'mobx';
import React, { Component } from 'react';
import Button from '@tencent/dui-mobile/lib/components/Button';
import Close from '@src/framework/frame/components/animation-editor/images/close.svg';
import ElementBaseModel from '@src/slide/domain/ui-model/element-modeles/base-modeles/element-base-model';
import Snackbar from '@tencent/dui/lib/components/Snackbar';
import Style from './index.less';
import { Tree } from 'antd';
import classNames from 'classnames';
import { getElementDetailDisplayInfo } from '@src/utils/get-display-name';
import i18n from '@i18next/i18n';
import { tdwReport, SELECT_WINDOW_REPORT } from './report';
import { SelectWindowShowAction } from '@src/js/constant';
import type GroupModel from '@src/slide/domain/ui-model/element-modeles/group-model';
import { IRightSideBarManager } from '@src/slide/application/service/right-side-bar-manager/right-side-bar-manager.interface';
import { IDrawingShapeDataModel } from '@tencent/slide-sdk-model/src/model/group-shape-data-model';
import { transaction } from '@src/slide/domain/slide-app';
import { ISelectWindowManagerServiceDelegates } from '@src/slide/application/service/select-window/core/select-window-manager.interface';
import { IRightSideBarService } from '../../../right-side-bar-service/core/interface';
import {
    IDrawerStateManager,
} from '@src/slide/application/service/drawer-state-manager/core/drawer-state-manager.interface';
import { DrawerName } from '@framework/workbench/drawer/constants';

const FIRST_LEVEL_KEY = '0';
const DEFAULT_DRAG_OPACITY_VALUE = 0.24;
const DEFAULT_INDEX = 1;

interface ShapeItem {
    // key 唯一不重复
    key: string;
    // 父元素的 key
    parentKey: string;
    // 树节点 title，支持 jsx
    title: JSX.Element;
    children: ShapeItem[];
    model: ElementBaseModel | GroupModel;
    // 父元素是否可选
    isParentDisableSelect: boolean;
    // 自身是否可以选，取决于 parentDisableSelect 或自身 hidden 属性
    disableSelect: boolean;
    // 树深度
    depth: number;
}

interface ShapeItemInfo {
    shapeItem?: ShapeItem;
    index?: number;
    parentShapeList?: ShapeItem[];
}

interface IProps {
    zIndex: number;
    services: ISelectWindowManagerServiceDelegates;
    drawerStateManager?: IDrawerStateManager;
}

export default class SelectWindow extends Component<IProps, {
    showSelectWindow?: boolean;
    shapeList: ShapeItem[];
    selectedShapeItems: ShapeItem[];
}> {
    private shapeListReaction: IReactionDisposer;
    private showSelectWindowReaction: IReactionDisposer;
    private selectedElementReaction: IReactionDisposer;
    private slideReaction: IReactionDisposer | null;
    private dragNode;
    private dragDom;
    private slideChildNum = 1; // 记录页面元素编号
    private slideChildrenMap = {}; // 缓存页面编号
    private services: ISelectWindowManagerServiceDelegates;

    public constructor(props) {
        super(props);
        this.services = props.services;
        const children = this.currentSlide?.children as ElementBaseModel[];
        const shapeList = this.getShapeList(children);
        this.state = {
            showSelectWindow: this.getRightSideBarManager()?.isSelectWindowShown,
            shapeList,
            selectedShapeItems: [],
        };
    }

    public componentDidMount() {
        this.showSelectWindowReaction = reaction(
            () => this.getRightSideBarManager()?.isSelectWindowShown,
            (showSelectWindow) => {
                const rightSideBarManager = this.getRightSideBarManager();
                if (showSelectWindow && rightSideBarManager) {
                    this.getRightSideBarService()?.hide();
                    tdwReport({
                        ...SELECT_WINDOW_REPORT.open,
                        ver5: this.getRightSideBarManager()?.selectWindowShowAction
                        === SelectWindowShowAction.FROM_FORMAT ? 2 : 1,
                        ver6: this.slideChildren?.length,
                    });
                }
                this.setState({ showSelectWindow: showSelectWindow || false });
            }, { fireImmediately: true },
        );
        // 根据外部的选中元素变化/元素组合信息发生改变，重建选中元素
        const rebuildSelectedShapeItems = (shapeList: ShapeItem[]) => {
            const selectedElements = this.selectedManager?.selectedArray;
            const selectedShapeItems: ShapeItem [] = [];
            if (selectedElements instanceof Array) {
                this.loop(shapeList, (shapeItem: ShapeItem) => {
                    if (selectedElements.includes(shapeItem.model)) {
                        selectedShapeItems.push(shapeItem);
                    }
                });
            }
            return selectedShapeItems;
        };
        // 用于监听当前页面 children 的变化
        this.shapeListReaction = reaction(
            () => this.getObserve(this.currentSlide?.children as ElementBaseModel[]),
            () => {
                this.unWatchTextRunChange();
                this.updateSlideChildrenNum(this.currentSlide?.children as ElementBaseModel[]);
                const shapeList = this.getShapeList(this.currentSlide?.children as ElementBaseModel[]);
                this.setState(
                    { selectedShapeItems: rebuildSelectedShapeItems(shapeList) },
                    // 第一次赋值 state 会造成 bug，请使用 Safari 打开
                    // https://iwiki.woa.com/tencent/static/iwiki-editor-pro/dist/preview.html?space=~ruiqitan&page=1049733000&id=103364&name=1.mov&size=2022035&language=zh
                    () => this.setState({ shapeList }, () => {
                        // mobx 监听不到文本变化，通过 cs 监听
                        this.watchTextRunChange();
                    }),
                );
            }, { fireImmediately: true },
        );

        // 用于监听当前页面选中元素的变化
        this.selectedElementReaction = reaction(
            () => this.getSelectionService()
                .selectedElements?.selectedArray?.map(shape => shape.id),
            () => {
                this.setState({ selectedShapeItems: rebuildSelectedShapeItems(this.state.shapeList) });
            }, { fireImmediately: true },
        );

        // 监听幻灯片切页
        this.slideReaction = reaction(
            () => this.getSelectionService().currentSlideIndex,
            () => {
                this.slideChildNum = DEFAULT_INDEX;
            },
        );
    }

    public componentWillUnmount() {
        this.showSelectWindowReaction();
        this.shapeListReaction();
        this.selectedElementReaction();
        this.unWatchTextRunChange();
        this.slideReaction?.();
    }

    public render() {
        if (!this.state.showSelectWindow) return null;
        return (<div className={classNames(Style['select-window'], [Style['select-window-drawer']])}>
            <div className={classNames(Style['select-window-header'], Style['select-window-header-drawer'])}>
                <div className={Style['select-window-header-title']} >{i18n.t('全部图层')}</div>
                <img className={Style['select-window-header-close']} src={Close} onClick={() => this.closeSelectWindow()}/>
            </div>
            <div className={Style['select-window-toolbar']}>
                <Button type="plain" onClick={this.handlerShowAll} className={Style.btn}>{i18n.t('全部显示')}</Button>
                <Button type="plain" onClick={this.handlerHideAll} className={Style.btn} style={{ marginLeft: '12px' }}>{i18n.t('全部隐藏')}</Button>
            </div>
            {this.renderTree()}
        </div>);
    }

    private renderTree = () => (<div className={classNames(Style['select-window-content-drawer'])}>
        <Tree className={classNames({
            [Style['select-window-tree']]: true,
            ['select-window-tree-global-drawer']: true,
        })}
        prefixCls="dui-tree"
        treeData={this.state.shapeList}
        selectedKeys={this.state.selectedShapeItems.map(shape => shape.key)}
        multiple
        blockNode
        draggable
        allowDrop={param => this.handlerAllowDrop(param)}
        onDrop={param => this.handlerOnDrop(param)}
        onDragStart={param => this.handlerOnDragStart(param)}
        onDragEnd={() => this.handleDragDomStyle()}
        onExpand={this.handleExpand}
        />
    </div>);

    private updateShapeList = () => {
        const children = this.currentSlide?.children as ElementBaseModel[];
        this.setState({ shapeList: this.getShapeList(children) });
    };

    /**
     * 使用 changeSetEngine 注册元素对文本的修改
     */
    private watchTextRunChange() {
        this.loop(this.state.shapeList, (child) => {
            child.model.changeSetEngine.addObserveForChangeAtPath('txBody', this.updateShapeList);
        });
    }

    /**
     * 使用 changeSetEngine 取消注册元素对文本的修改
     */
    private unWatchTextRunChange() {
        this.loop(this.state.shapeList, (child) => {
            child.model.changeSetEngine.removeObserveForChangeAtPath('txBody', this.updateShapeList);
        });
    }

    private findRootGroupShape(shapeModel) {
        let stackShape = shapeModel;
        while (![UIModelType.SLIDE, UIModelType.LAYOUT, UIModelType.MASTER].includes(stackShape?.parent?.type)) {
            stackShape = stackShape.parent;
        }
        return stackShape;
    }

    /**
     * 对 selectedManager 简单的二次封装
     * 实现目的：点击子元素时同时添加父元素
     * @param method
     * @param elements
     */
    private applySelectedManager(method, ...elements) {
        const stackElements: ElementBaseModel[] = [];
        elements.forEach((elementModel) => {
            stackElements.push(elementModel);
            stackElements.push(this.findRootGroupShape(elementModel));
        });
        this.selectedManager[method]?.(...stackElements);
    }

    private get selectedManager() {
        return this.getSelectionService().selectedElements;
    }

    private toggleDisplay(e: React.MouseEvent<HTMLElement, MouseEvent>, shapeItem) {
        e.stopPropagation();
        const { model: shape, parentKey, depth, key } = shapeItem;
        const toggleHidden = +!shape.hidden;
        //  如果是隐藏，则从选中元素中移除自己以及子元素
        if (toggleHidden) {
            this.state.selectedShapeItems.forEach((shapeItem) => {
                if (shapeItem.depth > depth || shapeItem.key === key) {
                    this.selectedManager.remove(shapeItem.model);
                }
            });
            this.handleHighlight(null);
            tdwReport(SELECT_WINDOW_REPORT.hideOne);
        } else {
            this.handleHighlight(shape.id);
            tdwReport(SELECT_WINDOW_REPORT.showOne);
        }
        // 等待 mobx 更新数据
        Promise.resolve().then(() => {
            // 如果是组合元素，移除选中该元素后，没有其他同级子元素选中，则移除跟组合元素
            if (!this.state.selectedShapeItems.find(shape => shape.parentKey === parentKey && shape.key !== key)) {
                this.selectedManager.remove(this.findRootGroupShape(shape));
            }
        });
        const nvPr = {
            hidden: !!toggleHidden,
        };
        (shape.sdkData as IDrawingShapeDataModel)?.api.updateProps({ nvPr });
    }

    /**
     * render 小眼睛
     * 如果父元素不可选，那么显隐操作也会被禁止，cursor也会更改
     * @param
     * @returns
     */
    private renderDisplayIcon(shapeItem: ShapeItem) {
        const { model, isParentDisableSelect: parentDisableSelect } = shapeItem;
        return (<i className={classNames({
            'display-icon': true,
            [Style['visible-icon']]: !model.hidden,
            [Style['hidden-icon']]: model.hidden,
            [Style['disabled-icon']]: parentDisableSelect,
        })} onClick={e => !parentDisableSelect && this.toggleDisplay(e, shapeItem)}></i>);
    }

    /**
     * 渲染树title的方法
     * 与本地PPT逻辑保持一致，第一层树节点 disable 但保持响应（选中/显隐）/非第一层树 enable 情况下不可选中
     * @param shapeItem
     * @returns
     */
    private renderTreeItemTitle(shapeItem: ShapeItem) {
        const disableClick = shapeItem.disableSelect && shapeItem.depth !== 0;
        const isGroupButNotRoot = shapeItem?.children?.length && shapeItem.depth !== 0;
        const handlerClick = (e) => {
            if (disableClick || isGroupButNotRoot) return;
            this.handlerTitleClick(e, shapeItem);
            this.handleHighlight(null);
        };
        const itemTitleClass = classNames({
            [Style['tree-item-title-container']]: true,
            [Style['tree-item-title-disable']]: disableClick,
        });
        const textClass = classNames(Style['tree-item-title-text'], {
            [Style['tree-item-title-text-hidden']]: shapeItem.model.hidden,
        });
        return <div className={itemTitleClass} onClick={handlerClick}
            onMouseEnter={() => this.handleTitleMouseEnter(shapeItem)}
            onMouseLeave={() => this.handleTitleMouseLeave()}
            onMouseDown={() => this.handleTitleMouseLeave()}>
            <span className={textClass}>{this.getTitleText(shapeItem)}</span>
            {this.renderDisplayIcon(shapeItem)}
        </div>;
    }

    private needStopTitleMouseEvent = (shapeItem?: ShapeItem) => {
        if (!shapeItem) return false;
        return typeof this.state?.selectedShapeItems.find(item => item === shapeItem
            || shapeItem.model === item.model?.rootGroupModel) !== 'undefined';
    };

    private handleTitleMouseEnter = (shapeItem: ShapeItem) => {
        if (this.needStopTitleMouseEvent(shapeItem)) return;
        this.handleHighlight(shapeItem.model.id);
    };

    private handleTitleMouseLeave = () => {
        if (this.needStopTitleMouseEvent()) return;
        this.handleHighlight(null);
    };

    private handleHighlight = (elementId: string | null) => {
        if (this.currentSlide) {
            this.currentSlide.highlightModelId = elementId;
        }
    };

    /**
     * 树节点的点击回调，提供多选键位支持
     */
    private handlerTitleClick(e: React.MouseEvent<HTMLSpanElement, MouseEvent>, shapeItem: ShapeItem) {
        e.stopPropagation();
        this.displayShapeItem(shapeItem);
        if (e.ctrlKey || e.metaKey) {
            this.handlerClickWithControl(shapeItem);
        } else {
            this.handlerClickWithOutControl(shapeItem);
        }
    }

    private displayShapeItem(shapeItem) {
        if (shapeItem.model.hidden !== 0) {
            const nvPr = {
                hidden: false,
            };
            (shapeItem.model.sdkData as IDrawingShapeDataModel)?.api.updateProps({ nvPr });
        }
    }

    /**
     * 返回渲染树的数据结构
     * @param originShapeList 原始 ElementBaseModel[]
     * @param parentKey 父节点 key 值
     * @param parentDisableSelect 父元素是否可选中
     * @returns
     */
    private getShapeList(
        originShapeList?: ElementBaseModel[],
        parentKey = FIRST_LEVEL_KEY,
        parentDisableSelect = false,
    ): ShapeItem[] {
        if (!originShapeList) return [];
        return originShapeList.slice().reverse()
            .map((shape, shapeIndex) => {
                const key = `${parentKey}-${shapeIndex}`;
                const disableSelect = parentDisableSelect || !!shape.hidden;
                const depth = parentKey.split('-').length - 1;
                const children = shape.children?.length
                    ? this.getShapeList(shape.children, `${parentKey}-${shapeIndex}`, disableSelect)
                    : [];
                const newShapeItem: ShapeItem = {
                    key,
                    parentKey,
                    children,
                    title: (<div></div>),
                    model: shape,
                    isParentDisableSelect: parentDisableSelect,
                    disableSelect,
                    depth,
                };
                newShapeItem.title = this.renderTreeItemTitle(newShapeItem);
                return newShapeItem;
            })
            .filter(({ model }) => !model.isGhostAttachment());
    }

    /**
     * 对 ShapeItem/ElementBaseModel[] 类型数据结构的遍历
     * @param shapeList
     * @param callback
     */
    private loop(shapeList, callback) {
        shapeList.forEach((child, index) => {
            if (child.children?.length) {
                this.loop(child.children, callback);
            }
            callback(child, index, shapeList);
        });
    }

    /**
     * 用于监听的数据结构，主要是 id/hidden/children 的变化
     * @param children
     * @returns
     */
    private getObserve(children?: Array<ElementBaseModel>) {
        return children?.map(child => ({
            id: child.id,
            hidden: child.hidden,
            children: child.children?.length ? this.getObserve(child.children) : [],
        }));
    }

    private closeSelectWindow() {
        this.getRightSideBarManager()?.setIsSelectWindowShown(false);
        this.props.drawerStateManager?.closeTab(DrawerName.SELECT_WINDOW_RIGHT_DRAWER);
    }

    private handlerOnDragStart({ node, event }) {
        this.dragNode = node;
        this.handleDragDomStyle(event?.target?.parentNode);

        this.applySelectedManager('add', node.model);
    }

    /**
     * 树节点拖拽是否允许释放的判定，对于调整 shape 组合的行为，暂不支持。
     * @param
     * @returns
     */
    private handlerAllowDrop({ dropNode, dropPosition }) {
        // dropPosition === 0，parentKey 相同时，是 dragNode 拖入 dropNode 的子元素，需要判定
        const isSameGroup = dropPosition !== 0 && dropNode.parentKey === this.dragNode.parentKey;
        // dropPosition === 0 时，是插入首行空白处，dropNode 会变成 dragNode 的父元素
        const isGroupFirstIndent = dropPosition === 0 && (dropNode.key === this.dragNode.parentKey);
        if (isSameGroup || isGroupFirstIndent) return true;
        return false;
    }

    private findShapeItem(key: string | Array<string>) {
        const keyIsArray = key instanceof Array;
        const shapeInfoArr: ShapeItemInfo[] = [];
        let shapeInfo;
        this.loop(this.state.shapeList, (shapeItem: ShapeItem, index, parentShapeList) => {
            if (keyIsArray && key.includes(shapeItem.key)) {
                shapeInfoArr.push({ shapeItem, index, parentShapeList });
            } else if (!keyIsArray && shapeItem.key === key) {
                shapeInfo = { shapeItem, index, parentShapeList };
            }
        });
        return keyIsArray ? shapeInfoArr : shapeInfo;
    }

    /**
     * 拖拽释放后的处理，与 handlerAllowDrop 类似
     * @param
     * @returns
     */
    private handlerOnDrop = ({ node: dropNode, dropPosition, dropToGap }) => {
        const dropPos = dropNode.pos.split('-');
        const stackDropPosition = dropPosition - dropPos[dropPos.length - 1];
        const dragNodeKeys = this.state.selectedShapeItems
            // 目的是为了去除选中态中的非同级元素
            .filter(shape => shape.parentKey === this.dragNode.parentKey)
            .map(shape => shape.key);
        const dragItemModelArr = this.findShapeItem(dragNodeKeys).map(({ shapeItem }) => shapeItem.model);
        const dropItemInfo = this.findShapeItem(dropNode.key);
        if (!dragItemModelArr.length || !dropItemInfo) return;

        // 拖拽到组内第一的位置和非第一的位置返还结果不一致
        // 拖拽到第一个：shapeItem 是 dropNode 的 parentNode，parentShapeList 是 dropNode 的 grandparent;
        // 非第一个：shapeItem 是 dropNode; parentShapeList 是 dropNode 的 parentNode;
        const { parentShapeList, index, shapeItem } = dropItemInfo;

        // rc-tree 提供的核心逻辑 https://tree-react-component.vercel.app/demo/animation-draggable
        // dropToGap false 的情况： 1.跨级拖拽 2.拖到到组内第一个
        // stackDropPosition -1 目前探究出来的是顶层拖拽到第一个间隙
        if (!dropToGap) {
            // 拖拽到组内第一的位置，toIndex 应该是 0，但这棵树是 reverse 过的，所以真实 toIndex 是 parentNode 的 lastChildIndex。
            this.currentSlide?.moveElement(shapeItem.children.length, dragItemModelArr, true);
        } else if (stackDropPosition === -1) {
            // 顶层拖拽到第一的位置，toIndex 应该是 0，但这棵树是 reverse 过的，所以真实 toIndex 是 parentNode 的 lastChildIndex。
            this.currentSlide
                ?.moveElement(parentShapeList.length - index, dragItemModelArr, true);
        } else {
            this.currentSlide
                ?.moveElement(parentShapeList.length - index - 1, dragItemModelArr, true);
        }
        this.selectedManager?.clear();
        tdwReport({ ...SELECT_WINDOW_REPORT.drag, ver6: this.slideChildren?.length });
    };


    /**
     * 用于校验多选时是否跨级多选
     * @param selectedNodes
     */
    private handlerClickWithControl(curShapeItem) {
        const isDisabled = new Set([
            ...this.state.selectedShapeItems.map(shape => shape.parentKey),
            curShapeItem.parentKey,
        ]).size > 1;
        if (isDisabled) {
            Snackbar.show({
                message: i18n.t('暂不支持跨级多选'),
                autoClose: true,
                closable: true,
            });
            return;
        }
        this.toggleSelectShapeItem(curShapeItem);
    }

    private handlerClickWithOutControl(shapeItem) {
        const { selectedShapeItems } = this.state;
        this.selectedManager?.clear();
        // 当选中元素个数不为1或者不包含这个元素
        if (selectedShapeItems.length !== 1 || !selectedShapeItems.includes(shapeItem)) {
            this.applySelectedManager('add', shapeItem.model);
        }
    }

    private toggleSelectShapeItem(curShapeItem) {
        const { selectedShapeItems } = this.state;
        const newSelectedShapeItems: ShapeItem[] = selectedShapeItems.includes(curShapeItem)
            ? selectedShapeItems.filter(shapeItem => shapeItem !== curShapeItem)
            : [...selectedShapeItems, curShapeItem];
        this.selectedManager?.clear();
        newSelectedShapeItems.forEach(node => this.applySelectedManager('add', node.model));
    }

    private handlerShowAll = () => {
        tdwReport(SELECT_WINDOW_REPORT.showAll);
        transaction(() => {
            this.loop(this.state.shapeList, (shapeItem: ShapeItem) => {
                if (shapeItem.model.hidden) {
                    const nvPr = {
                        hidden: false,
                    };
                    // @ts-ignore
                    (shapeItem.model.sdkData as IDrawingShapeDataModel)?.api.updateProps({ nvPr });
                }
            });
        });
    };

    private handlerHideAll = () => {
        tdwReport(SELECT_WINDOW_REPORT.hideAll);
        this.selectedManager?.clear();
        transaction(() => {
            this.state.shapeList.forEach((child) => {
                if (!child.model.hidden) {
                    const nvPr = {
                        hidden: true,
                    };
                    // @ts-ignore
                    (child.model.sdkData as IDrawingShapeDataModel)?.api.updateProps({ nvPr });
                }
            });
        });
    };

    private handleDragDomStyle = (dom?) => {
        if (dom) {
            this.dragDom = dom;
            this.dragDom.style.opacity = DEFAULT_DRAG_OPACITY_VALUE;
        } else {
            if (!this.dragDom) return;
            this.dragDom.style.opacity = '';
            this.dragDom = null;
        }
    };

    private handleExpand = (expandedKeys, { expanded }) => {
        tdwReport({
            ...(expanded ? SELECT_WINDOW_REPORT.unFoldGroup : SELECT_WINDOW_REPORT.foldGroup),
            ver6: this.slideChildren?.length,
        });
    };

    private getTitleText = (shapeItem: ShapeItem) => {
        const { name } = getElementDetailDisplayInfo({ elementModel: shapeItem.model });
        const index = name.indexOf(':');
        const modelIndex = this.slideChildrenMap[this.currentSlide?.id || '']?.[shapeItem.model.id ?? shapeItem.model.key]
            ?? DEFAULT_INDEX;
        const reg = /\x20\d+$/;
        if (index === -1) {
            return `${name.replace(reg, '')} ${modelIndex}`;
        }
        return `${name.slice(0, index).replace(reg, '')} ${modelIndex}${name.slice(index)}`;
    };

    /**
     * 更新slideChildNum为页面上最大编号值+1
     */
    private updateSlideChildrenNum = (children: ElementBaseModel[]): void => {
        children?.forEach((shape: ElementBaseModel) => {
            const modelIndex = this.slideChildrenMap[this.currentSlide.id || '']?.[shape.id ?? shape.key];
            if (typeof modelIndex !== 'undefined') {
                this.slideChildNum = Math.max(this.slideChildNum, modelIndex + 1);
            }
            if (shape.children?.length > 0) {
                this.updateSlideChildrenNum(shape.children);
            }
            this.updateModelIndex(shape);
        });
    };

    /**
     * 更新元素编号
     */
    private updateModelIndex = (shape: ElementBaseModel) => {
        if (!this.slideChildrenMap[this.currentSlide.id || '']) {
            this.slideChildrenMap[this.currentSlide.id || ''] = {};
        }
        const currentSlideMap = this.slideChildrenMap[this.currentSlide.id || ''];
        const modelId = shape.id ?? shape.key;
        if (!currentSlideMap[modelId]) {
            currentSlideMap[modelId] = this.slideChildNum;
            this.slideChildNum += 1;
        }
    };

    private get slideChildren() {
        return this.currentSlide?.children;
    }

    private get currentSlide() {
        return this.getSelectionService().currentSlide!;
    }

    private getRightSideBarManager = ():
        IRightSideBarManager | undefined => this.services.rightSideBarManager;

    private getSelectionService = () => this.services.selectionService;
    private getRightSideBarService = ():
        IRightSideBarService | undefined => this.services.rightSideBarService;
}
